import moment from "moment";
import * as XLSX from "xlsx";

export interface HeaderExtractionResult {
  success: boolean;
  headers: string[];
  message: string;
  data: Record<string, any>[]; // <-- unified type
}

/**
 * Extract headers from uploaded Excel or CSV file
 * @param file - The uploaded file (Excel or CSV)
 * @returns Promise<HeaderExtractionResult>
 */
export const extractHeadersFromFileV2 = async (
  file: File
): Promise<HeaderExtractionResult> => {
  try {
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!fileExtension || !["xlsx", "xls", "csv"].includes(fileExtension)) {
      return {
        success: false,
        headers: [],
        message:
          "Unsupported file format. Please upload Excel (.xlsx, .xls) or CSV (.csv) files only.",
          data: []
      };
    }

    // Read file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();

    if (fileExtension === "csv") {
      return await extractHeadersFromCSV(arrayBuffer);
    } else {
      return await extractHeadersFromExcel(arrayBuffer);
    }
  } catch (error) {
    console.error("Error extracting headers:", error);
    return {
      success: false,
      headers: [],
      message:
        "Failed to extract headers from file. Please ensure the file is not corrupted.",
        data: []
    };
  }
};

/**
 * Extract headers + rows from Excel file
 */
const extractHeadersFromExcel = async (
  arrayBuffer: ArrayBuffer
): Promise<HeaderExtractionResult> => {
  try {
   
    const workbook = XLSX.read(arrayBuffer, {
  type: "array",
  cellDates: true, // <--- important
});
    const firstSheetName = workbook.SheetNames[0];

    if (!firstSheetName) {
      return {
        success: false,
        headers: [],
        message: "No sheets found in the Excel file.",
        data: []
      };
    }

    const worksheet = workbook.Sheets[firstSheetName];
    const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, { header: 1, raw: false,
  dateNF: "dd/mm/yyyy" });
    const formattedData = jsonData;

    if (formattedData.length === 0) {
      return {
        success: false,
        headers: [],
        message: "The Excel file appears to be empty.",
        data: []
      };
    }

    // Extract headers
    const headers = (formattedData[0] as string[])
      .map((h) => (h ? h.toString().trim() : ""))
      .filter((h) => h !== "");

    if (headers.length === 0) {
      return {
        success: false,
        headers: [],
        message: "No headers found in the first row of the Excel file.",
        data: []
      };
    }

    // Normalize rows → objects
    const data: Record<string, any>[] = formattedData.slice(1).map((row: any[]) => {
      const obj: Record<string, any> = {};
      headers.forEach((header, i) => {
        obj[header] = row[i] ?? "";
      });
      return obj;
    });

    return {
      success: true,
      headers,
      message: "Headers extracted successfully from Excel file.",
      data,
    };
  } catch (error) {
    console.error("Error processing Excel file:", error);
    return {
      success: false,
      headers: [],
      message:
        "Failed to process Excel file. Please ensure the file is not corrupted.",
        data: []
    };
  }
};

/**
 * Extract headers + rows from CSV file
 */
const extractHeadersFromCSV = async (
  arrayBuffer: ArrayBuffer
): Promise<HeaderExtractionResult> => {
  try {
    let csvString = new TextDecoder("utf-8").decode(arrayBuffer);
    csvString = csvString.replace(/^\ufeff/, ""); // strip BOM if present
    const lines = csvString.split(/\r?\n/).filter((line) => line.trim() !== "");

    if (lines.length === 0) {
      return {
        success: false,
        headers: [],
        message: "The CSV file appears to be empty.",
        data: []
      };
    }

    // Detect delimiter (default: ",")
    const delimiter = [",", ";", "\t"].find((d) => lines[0].includes(d)) || ",";

    // Extract headers
    const headers = lines[0]
      .split(delimiter)
      .map((h) => h.replace(/^"|"$/g, "").trim())
      .filter((h) => h !== "");

    if (headers.length === 0) {
      return {
        success: false,
        headers: [],
        message: "No headers found in the first row of the CSV file.",
        data: []
      };
    }

    // Normalize rows → objects
    const data: Record<string, any>[] = lines.slice(1).map((line) => {
      const values = line
        .split(delimiter)
        .map((v) => v.replace(/^"|"$/g, "").trim());
      const obj: Record<string, any> = {};
      headers.forEach((header, i) => {
        obj[header] = values[i] ?? "";
      });
      return obj;
    });

    return {
      success: true,
      headers,
      message: "Headers extracted successfully from CSV file.",
      data,
    };
  } catch (error) {
    console.error("Error processing CSV file:", error);
    return {
      success: false,
      headers: [],
      message:
        "Failed to process CSV file. Please ensure the file is not corrupted.",
        data: []
    };
  }
};

/**
 * Validate if required fields are present in the extracted headers
 * @param headers - Array of extracted headers
 * @param requiredFields - Array of required field names
 * @returns Object with validation result
 */
export const validateRequiredFields = (
  headers: string[],
  requiredFields: string[]
): { isValid: boolean; missingFields: string[] } => {
  const missingFields = requiredFields.filter(
    (field) =>
      !headers.some((header) => header.toLowerCase() === field.toLowerCase())
  );

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
};
